import type { ReactNode } from 'react'
import { useEffect } from 'react'

import { useAtom } from 'jotai'
import { useRouter } from 'next/router'
import { equals, isNil } from 'ramda'

import { checkoutRegistrationPrefillParam } from '@twisto/base/checkout-registration-prefill'
import { remoteLanguageAtom } from '@twisto/base/remote-language'
import { useGetFlowData } from '@twisto/flow-views'
import {
  analytics,
  getAffiliateQueryString,
  getSessionAffiliateQueryParams,
} from '@twisto/utils'

import { useFlowBrowserEvent } from 'flow/components/flow-handler/flow-browser-event.hook'

import { getFlowStepRoute } from './flow-step-route'

const pathWithoutQueryOrHash = (path: string) =>
  path.split('?')[0].split('#')[0]

const withTrailingSlash = (path: string) =>
  path === '/' || path.endsWith('/') ? path : `${path}/`

type Props = {
  children: ReactNode
}
export const FlowHandler = ({ children }: Props) => {
  const { query, push, replace, asPath } = useRouter()
  const sessionId = query.sessionId as string

  const { flow } = useGetFlowData(sessionId)

  const step = flow?.currentStep.__typename
  const nextStepPath = getFlowStepRoute({ step, sessionId })
  const [store, setRemoteLanguage] = useAtom(remoteLanguageAtom)
  const userLanguage = flow?.userLanguage

  useEffect(() => {
    // If we have saved userLanguage in the localStorage and received userLanguage.language is null don't override the localStorage
    const isReceivedLanguageSet = !isNil(userLanguage?.language)
    const isUserLanguageInStore = !isNil(store?.userLanguage)

    if (
      !isNil(userLanguage) &&
      isReceivedLanguageSet &&
      isUserLanguageInStore &&
      !equals(userLanguage, store.userLanguage)
    ) {
      setRemoteLanguage({
        userLanguage,
      })
    }
  }, [setRemoteLanguage, store, userLanguage])

  useFlowBrowserEvent()

  /**
   * on a new step, we send postMessage if it's available
   * and if we have a merchantOrigin URL (because of the data safety)
   * and if the window has a different parent
   */

  useEffect(() => {
    if (!step) {
      return
    }

    const current = withTrailingSlash(pathWithoutQueryOrHash(asPath))
    const next = withTrailingSlash(nextStepPath)
    const affiliateQueryParams = getSessionAffiliateQueryParams(
      sessionId,
      query,
      sessionStorage
    )
    const affiliateQueryString = getAffiliateQueryString(affiliateQueryParams)
    const checkoutRegistrationPrefill = query[checkoutRegistrationPrefillParam]
    const routeQueryParams = {
      ...affiliateQueryParams,
      ...(checkoutRegistrationPrefill === undefined
        ? {}
        : {
            [checkoutRegistrationPrefillParam]: checkoutRegistrationPrefill,
          }),
    }

    if (affiliateQueryString) {
      analytics.setGlobalData({ query: affiliateQueryString })
    }

    if (current !== next) {
      push({
        pathname: nextStepPath,
        query: routeQueryParams,
      })

      return
    }

    if (!window.location.search && affiliateQueryString) {
      replace(
        {
          pathname: nextStepPath,
          query: routeQueryParams,
        },
        undefined,
        { scroll: false }
      )
    }
  }, [step, nextStepPath, asPath, push, query, replace, sessionId])

  return <>{children}</>
}
